import { useCallback } from 'react';
import { useClient } from 'urql';
import { type DeliveryOrderDetail, type Order } from '@sg/graphql-schema';

import {
  getLocalStorageOrderId,
  type OrderingContext,
} from '@order/features/ordering';

import { type PartialLineItem } from '../../../../graphql';
import { LastOrderDateDocument } from '../../../../hooks';
import {
  BagPostOrderDocument,
  type BagPostOrderQuery,
} from '../../GraphQL/BagPostOrder.generated';
import { prepareCart } from '../../utils';

/**
 * With frontend intent confirmation, the backend will place the order on its own.
 * To ensure the cart is cleared and to know whether the order has earned loyalty points, we'll fetch the order status.
 */
export const usePostOrderFetch = () => {
  const client = useClient();

  const fetchStatePostOrder = useCallback(
    async (context: OrderingContext<PartialLineItem, DeliveryOrderDetail>) => {
      const orderId = context.orderId || (await getLocalStorageOrderId());
      const bagPostOrderQuery = client.query<BagPostOrderQuery>;

      if (!orderId) throw new Error('Order ID is required');

      const response = await bagPostOrderQuery(
        BagPostOrderDocument,
        { id: orderId },
        { requestPolicy: 'network-only' },
      ).toPromise();

      const maybeOrderStatus = response.data?.orderStatus;

      if (maybeOrderStatus?.__typename === 'InvalidOrder') {
        throw new Error('Invalid order');
      }

      const order = maybeOrderStatus?.order;

      if (!order) {
        throw new Error('Order not found');
      }

      await updateLastOrderCache(client.query);

      return {
        order,
        cart: prepareCart(order as Partial<Order>),
      };
    },
    [client.query],
  );

  return { fetchStatePostOrder };
};

/**
 * Here we update the last order cache after a order is recognized.
 */
async function updateLastOrderCache(
  query: ReturnType<typeof useClient>['query'],
) {
  await query(
    LastOrderDateDocument,
    { input: { page: 1, take: 1 } },
    { requestPolicy: 'cache-and-network' },
  ).toPromise();
}
